import React, { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Plus, Edit2, Trash2, Mail, Phone, User, Search } from 'lucide-react';
import { toast } from 'sonner';
import { sortMembers, ROLE_PRIORITY } from '../sortUtils';

const ROLES = [
  { key: 'chair', label: 'Chair', color: '#6B3FA0' },
  { key: 'vice_chair', label: 'Vice Chair', color: '#9B7ED8' },
  { key: 'treasurer', label: 'Treasurer', color: '#43A047' },
  { key: 'secretary', label: 'Secretary', color: '#3B82F6' },
  { key: 'director', label: 'Director', color: '#8E85A0' },
];
const empty = () => ({ name: '', role: 'director', email: '', phone: '', bio: '', term_start: '', term_end: '', committees: [], user_id: '', is_active: true, create_login: false });

export default function DirectoryTab({ api, isAdmin, members, onChange }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [credentialsModal, setCredentialsModal] = useState(null); // { email, temp_password }

  const openCreate = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...empty(), ...m, committees: m.committees || [] }); setOpen(true); };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortMembers(
      (members || [])
        .filter(m => roleFilter === 'all' || m.role === roleFilter)
        .filter(m => !q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q) || (m.committees || []).some(c => c.toLowerCase().includes(q)))
    );
  }, [members, search, roleFilter]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      let memberRecord = editing;
      if (editing) {
        await api.put(`/board/members/${editing.id}`, form);
      } else {
        const { data } = await api.post('/board/members', form);
        memberRecord = data;
      }
      // Optional one-shot: create a board_director login
      if (!editing && form.create_login) {
        if (!form.email.trim()) {
          toast.error('Member created, but email is required to create a login');
        } else {
          try {
            const { data: creds } = await api.post(`/board/members/${memberRecord.id}/create-login`, {});
            setCredentialsModal(creds);
          } catch (e) {
            toast.error(`Login not created: ${e.response?.data?.detail || 'unknown'}`);
          }
        }
      }
      toast.success('Saved');
      setOpen(false);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  const grantLoginForExisting = async (m) => {
    if (!m.email) { toast.error('Add an email to this director first, then try again'); return; }
    if (m.user_id) { toast.error('This director already has a login'); return; }
    if (!window.confirm(`Create a Board Director login for ${m.name}? A temporary password will be generated.`)) return;
    try {
      const { data: creds } = await api.post(`/board/members/${m.id}/create-login`, {});
      setCredentialsModal(creds);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create login');
    }
  };
  const del = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the board?`)) return;
    try { await api.delete(`/board/members/${m.id}`); toast.success('Removed'); onChange(); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="directory-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#2D1B4E]">Board Members</h2>
          <p className="text-xs text-[#5A4D6E]">
            {members.length} active director{members.length !== 1 ? 's' : ''}
            {filtered.length !== members.length && ` · ${filtered.length} match${filtered.length !== 1 ? 'es' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-2 text-[#8E85A0]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, committee…"
              className="pl-7 h-8 text-xs w-56"
              data-testid="directory-search"
            />
          </div>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
            className="border border-[#DED8EB] rounded px-2 py-1.5 text-xs bg-white" data-testid="directory-role-filter">
            <option value="all">All roles</option>
            {ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          {isAdmin && (
            <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8F]" data-testid="add-member-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Director
            </Button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-center py-12 text-[#8E85A0] text-sm">
          {members.length === 0 ? 'No board members yet.' : 'No members match your search.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(m => {
            const role = ROLES.find(r => r.key === m.role) || ROLES[ROLES.length - 1];
            return (
              <Card key={m.id} className="border-[#DED8EB] bg-white" data-testid={`member-card-${m.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {m.photo_b64 ? (
                      <img src={m.photo_b64.startsWith('data:') ? m.photo_b64 : `data:image/jpeg;base64,${m.photo_b64}`}
                        alt={m.name} className="h-12 w-12 rounded-full object-cover" />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-[#EDE7F6] flex items-center justify-center text-[#6B3FA0] font-bold text-lg">
                        {(m.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#2D1B4E] truncate">{m.name}</p>
                      <Badge className="text-[10px] mt-0.5" style={{ background: `${role.color}22`, color: role.color, borderColor: role.color }}>
                        {role.label}
                      </Badge>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(m)} className="h-7 w-7" data-testid={`edit-member-${m.id}`}><Edit2 className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => del(m)} className="h-7 w-7 text-[#D9534F]" data-testid={`delete-member-${m.id}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-[#5A4D6E]">
                    {m.email && <p className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> <a href={`mailto:${m.email}`} className="hover:underline truncate">{m.email}</a></p>}
                    {m.phone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> <a href={`tel:${m.phone}`} className="hover:underline">{m.phone}</a></p>}
                    {(m.term_start || m.term_end) && (
                      <p className="text-[10px] text-[#8E85A0] mt-1">Term: {m.term_start || '—'} → {m.term_end || '—'}</p>
                    )}
                    {(m.committees || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {m.committees.map((c, i) => <Badge key={i} className="text-[9px] bg-[#F0EDF6] text-[#6B3FA0]">{c}</Badge>)}
                      </div>
                    )}
                  </div>
                  {m.bio && <p className="text-[11px] italic text-[#8E85A0] mt-2 line-clamp-3">{m.bio}</p>}
                  {isAdmin && !m.user_id && m.email && (
                    <button
                      onClick={() => grantLoginForExisting(m)}
                      className="mt-2 text-[10px] font-bold text-[#6B3FA0] hover:text-[#5A2F8F] underline decoration-dotted underline-offset-2"
                      data-testid={`grant-login-${m.id}`}>
                      + Create login for this director
                    </button>
                  )}
                  {m.user_id && (
                    <p className="mt-2 text-[10px] text-[#1B5E20] font-medium flex items-center gap-1">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#43A047]" /> Has portal login
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="member-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Board Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="m-name" />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="m-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="m-email" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Term Start</Label>
                <Input type="date" value={form.term_start} onChange={e => setForm(f => ({ ...f, term_start: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Term End</Label>
                <Input type="date" value={form.term_end} onChange={e => setForm(f => ({ ...f, term_end: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Committees (comma-separated)</Label>
              <Input value={(form.committees || []).join(', ')} onChange={e => setForm(f => ({ ...f, committees: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} placeholder="Finance, Governance" />
            </div>
            <div>
              <Label className="text-xs">Linked User ID (for login + own-record edits)</Label>
              <Input value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))} placeholder="optional" />
            </div>
            <div>
              <Label className="text-xs">Bio</Label>
              <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={3}
                className="w-full border border-[#DED8EB] rounded px-2 py-1 text-sm" />
            </div>
            {!editing && (
              <div className="border-t border-[#F0EDF6] pt-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.create_login}
                    onChange={e => setForm(f => ({ ...f, create_login: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 accent-[#6B3FA0]"
                    data-testid="create-login-checkbox"
                  />
                  <div>
                    <p className="text-sm font-medium text-[#2D1B4E]">Also create a login for this director</p>
                    <p className="text-[11px] text-[#8E85A0] leading-snug">
                      Creates a Board Director account with a temporary password (shown after save). They'll see only the Board Portal — no scheduling or staff tabs. Requires an email above.
                    </p>
                  </div>
                </label>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#6B3FA0]" data-testid="save-member-btn">{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credentials reveal modal — shown once after a login is created */}
      <Dialog open={!!credentialsModal} onOpenChange={(o) => !o && setCredentialsModal(null)}>
        <DialogContent className="max-w-md" data-testid="director-credentials-modal">
          <DialogHeader><DialogTitle>Director login created</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-[#5A4D6E]">Share these credentials with the director. They should change the password on first login.</p>
            <div className="bg-[#F7F4EF] border border-[#DED8EB] rounded p-3 font-mono text-xs">
              <div><span className="text-[#8E85A0]">Email:</span> <strong className="text-[#2D1B4E]">{credentialsModal?.email}</strong></div>
              <div className="mt-1"><span className="text-[#8E85A0]">Temp password:</span> <strong className="text-[#2D1B4E]">{credentialsModal?.temp_password}</strong></div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (credentialsModal) navigator.clipboard?.writeText(`Email: ${credentialsModal.email}\nPassword: ${credentialsModal.temp_password}`);
                toast.success('Copied to clipboard');
              }}
              data-testid="copy-credentials-btn">
              Copy email + password
            </Button>
            <p className="text-[10px] text-[#D9534F]">⚠ This is the only time the password is shown. Save it now.</p>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setCredentialsModal(null)} className="bg-[#6B3FA0]">Got it</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
